import * as React from "react"
import Link from "next/link"
import {cn} from "@/lib/utils"
import {ButtonProps, buttonVariants} from "@/components/ui/button"
import {ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon} from "lucide-react"

const Pagination = React.forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<"nav">
>(({className, ...props}, ref) => (
    <nav
        ref={ref}
        role="navigation"
        aria-label="pagination"
        className={cn("mx-auto flex w-full justify-center", className)}
        {...props}
    />
))
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef<
    HTMLUListElement,
    React.ComponentPropsWithoutRef<"ul">
>(({className, ...props}, ref) => (
    <ul
        ref={ref}
        className={cn("flex flex-row items-center gap-1", className)}
        {...props}
    />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef<
    HTMLLIElement,
    React.ComponentPropsWithoutRef<"li">
>(({className, ...props}, ref) => (
    <li ref={ref} className={cn(className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

type PaginationLinkProps = {
    isActive?: boolean
    href: string
} & Pick<ButtonProps, "size"> &
    Omit<React.ComponentPropsWithoutRef<typeof Link>, "href">

const PaginationLink = React.forwardRef<HTMLAnchorElement, PaginationLinkProps>(
    ({className, isActive, href, size = "icon", ...props}, ref) => (
        <Link
            ref={ref}
            href={href}
            aria-current={isActive ? "page" : undefined}
            className={cn(
                buttonVariants({
                    variant: isActive ? "outline" : "ghost",
                    size,
                }),
                size === "icon" ? `${isActive && "bg-blue-secondary text-white"} !border-0 hover:bg-blue-secondary hover:text-white  me-2  text-sm md:text-xl` : "",
                className
            )}
            {...props}
        />
    )
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = React.forwardRef<
    React.ElementRef<typeof PaginationLink>,
    React.ComponentPropsWithoutRef<typeof PaginationLink>
>(({className, ...props}, ref) => (
    <PaginationLink
        ref={ref}
        aria-label="Go to previous page"
        size="default"
        className={cn("hover:!bg-white", className)}
        {...props}
    >
        <ChevronRightIcon
            className="border-black scale-[1.3] p-[.8px] w-5 h-5 border rounded-full transition-all"/>
        <span className="sr-only">Previous</span>
    </PaginationLink>
))
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = React.forwardRef<
    React.ElementRef<typeof PaginationLink>,
    React.ComponentPropsWithoutRef<typeof PaginationLink>
>(({className, ...props}, ref) => (
    <PaginationLink
        ref={ref}
        aria-label="Go to next page"
        size="default"
        className={cn("hover:!bg-white", className)}
        {...props}
    >
        <span className="sr-only">Next</span>
        <ChevronLeftIcon
            className="w-5 h-5 border-black scale-[1.3] p-[.8px] border rounded-full transition-all"/>
    </PaginationLink>
))
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({
                                className,
                                ...props
                            }: React.ComponentProps<"span">) => (
    <span
        aria-hidden
        className={cn("flex h-9 w-9 items-center justify-center", className)}
        {...props}
    >
    <MoreHorizontalIcon className="h-4 w-4"/>
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

export {
    Pagination,
    PaginationContent,
    PaginationLink,
    PaginationItem,
    PaginationPrevious,
    PaginationNext,
    PaginationEllipsis,
}